// src/hooks/useMessagePermissions.ts
//
// Capability signals for the Messages surface (the Unibox / Contact
// communications inbox).
//
// Mirrors `useProjectPermissions` and `useContactCompanyPermissions` exactly —
// same shape, same fail-closed behaviour, same prohibition on role
// comparisons. There is no new authorization abstraction here; this is the
// established pattern applied to one more domain.
//
// MECHANISM — Central capabilities, never roles.
//
//   Central staging publishes `crm.messages` with THREE actions, measured
//   from the live payload before this hook was written:
//
//     role     read   write   admin
//     owner    true   true    true
//     admin    true   true    false
//     member   true   true    false
//     viewer   true   false   false
//
//   `delete`, `send`, `create` and `update` are ABSENT — not false, absent.
//   Asking for one would resolve to false for every role including owners and
//   silently hide the surface, so every member-safe mutation maps to `write`.
//
// WHY `write` IS THE HONEST MAPPING FOR BOTH BACKEND PERMISSIONS
//
//   CRM's backend guards this surface with `messages.send` (create thread,
//   send, retry) and `threads.manage` (update thread, add/remove
//   participant). Both carry the role set {owner, admin, member} — precisely
//   the set Central grants `messages.write`. So one frontend boolean cannot
//   over- or under-promise relative to either server rule.
//
//   That equivalence is asserted in the tests. If the backend ever splits
//   those two permissions apart, the mapping stops being lossless and this
//   hook must gain a second signal rather than keep aliasing.
//
// NOT MAPPED: `messages.admin`. It exists (owner-only) but nothing on this
// surface requires owner-only authority, and gating a member-safe control on
// it would deny members something the backend permits.
//
// THE SERVER REMAINS AUTHORITATIVE. These flags decide what is RENDERED;
// `requirePermission` decides what is PERMITTED. A stale or over-generous
// capability map can only ever produce a visible control that then 403s.

import { useCentralContext, useHasCapability } from '../services/centralContext';
import { FEATURE_CAPABILITIES } from '../config/featureCapabilities';

export interface MessagePermissions {
  /** `messages.read` — may see the Messages surface and read permitted threads. */
  canViewMessages: boolean;
  /**
   * `messages.write` — may send, create threads, and manage thread
   * participants. Covers BOTH backend `messages.send` and `threads.manage`,
   * which share a role set.
   */
  canWriteMessages: boolean;
  /** Alias of {@link canWriteMessages}, for send/compose call sites. */
  canSendMessage: boolean;
  /** Alias of {@link canWriteMessages}, for thread create/edit call sites. */
  canManageThread: boolean;
  /**
   * False until the Central context resolves. Distinct from a resolved
   * denial: callers can show a skeleton rather than an empty-permission
   * screen, and nothing can flash enabled and then retract.
   */
  isResolved: boolean;
}

export function useMessagePermissions(): MessagePermissions {
  const context = useCentralContext();

  const canViewMessages = useHasCapability(
    FEATURE_CAPABILITIES.viewMessages.domain,
    FEATURE_CAPABILITIES.viewMessages.action,
  );
  // Every write-ish affordance resolves through the SAME capability question.
  // Asking once and aliasing keeps the aliases honest — they cannot drift
  // apart into separate, subtly different checks.
  const canWriteMessages = useHasCapability(
    FEATURE_CAPABILITIES.sendMessage.domain,
    FEATURE_CAPABILITIES.sendMessage.action,
  );

  return {
    canViewMessages,
    canWriteMessages,
    canSendMessage: canWriteMessages,
    canManageThread: canWriteMessages,
    isResolved: context !== null,
  };
}
